import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    PhotoIcon,
    TrashIcon,
    DevicePhoneMobileIcon,
    ComputerDesktopIcon,
    TagIcon,
    FolderIcon,
    ArrowUpTrayIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { API } from '../../config';

const CollectionBannerTab = () => {
    const [desktopBanner, setDesktopBanner] = useState(null);
    const [mobileBanner, setMobileBanner] = useState(null);
    const [desktopPreview, setDesktopPreview] = useState(null);
    const [mobilePreview, setMobilePreview] = useState(null);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);

    useEffect(() => {
        fetchCategories();
        fetchAllSubcategories();
        fetchBanners();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            fetchSubcategories(selectedCategory);
        }
    }, [selectedCategory]);

    // Cleanup previews
    useEffect(() => {
        return () => {
            if (desktopPreview) URL.revokeObjectURL(desktopPreview);
            if (mobilePreview) URL.revokeObjectURL(mobilePreview);
        };
    }, [desktopPreview, mobilePreview]);

    const fetchAllSubcategories = async () => {
        try {
            const response = await axios.get(`${API}/subcategories`);
            setSubcategories(response.data);
        } catch (error) {
            setSubcategories([]);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API}/categories`);
            setCategories(response.data);
        } catch (error) {
            setCategories([]);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            const response = await axios.get(`${API}/subcategories?category_id=${categoryId}`);
            setSubcategories(response.data);
        } catch (error) {
            setSubcategories([]);
        }
    };

    const fetchBanners = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/collection-banners`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBanners(response.data);
        } catch (error) {
            setBanners([]);
        }
    };

    const handleDesktopChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setDesktopBanner(file);
            setDesktopPreview(URL.createObjectURL(file));
        }
    };

    const handleMobileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setMobileBanner(file);
            setMobilePreview(URL.createObjectURL(file));
        }
    };

    const handleDeleteBanner = async (id) => {
        if (!window.confirm('Delete this banner?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/collection-banners/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Banner deleted successfully!');
            fetchBanners();
        } catch (error) {
            toast.error('Failed to delete banner.');
        }
    };

    const resetForm = () => {
        setDesktopBanner(null);
        setMobileBanner(null);
        setDesktopPreview(null);
        setMobilePreview(null);
        setSelectedCategory("");
        setSelectedSubcategory("");
        setShowAddForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!selectedCategory || !selectedSubcategory || !desktopBanner || !mobileBanner) {
            toast.error("Please fill all fields and select images.");
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append("category_id", selectedCategory);
            formData.append("subcategory_id", selectedSubcategory);
            formData.append("desktop_banner", desktopBanner);
            formData.append("mobile_banner", mobileBanner);

            await axios.post(`${API}/collection-banners`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success("Banner uploaded successfully!");
            resetForm();
            fetchBanners();
        } catch (err) {
            toast.error("Error uploading banner. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm overflow-hidden"
        >
            <div className="p-8 pb-0">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Collection Banners</h2>
                        <p className="text-gray-500 text-sm mt-1">Manage banners for specific categories and subcategories</p>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowAddForm(true)}
                        className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm hover:shadow-md flex items-center font-medium text-sm"
                    >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Banner
                    </motion.button>
                </div>
            </div>

            <div className="px-8">
                <AnimatePresence>
                    {showAddForm && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden mb-8"
                        >
                            <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                                        <PlusIcon className="h-5 w-5 mr-2 text-gray-700" />
                                        Add New Banner
                                    </h3>
                                    <button
                                        onClick={resetForm}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-8">
                                    <div className="grid md:grid-cols-2 gap-8">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                                <TagIcon className="h-4 w-4 mr-1.5" />
                                                Category
                                            </label>
                                            <select
                                                value={selectedCategory}
                                                onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubcategory(''); }}
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center">
                                                <FolderIcon className="h-4 w-4 mr-1.5" />
                                                Subcategory
                                            </label>
                                            <select
                                                value={selectedSubcategory}
                                                onChange={(e) => setSelectedSubcategory(e.target.value)}
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                                disabled={!selectedCategory}
                                            >
                                                <option value="">Select Subcategory</option>
                                                {subcategories.filter(sub => sub.category_id === selectedCategory).map((sub) => (
                                                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-8">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center">
                                                <ComputerDesktopIcon className="h-4 w-4 mr-1.5" />
                                                Desktop Banner
                                            </label>
                                            <div className="relative group cursor-pointer">
                                                <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all h-48 flex flex-col justify-center items-center ${desktopPreview ? 'border-gray-300 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
                                                    }`}>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleDesktopChange}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    />
                                                    {desktopPreview ? (
                                                        <div className="relative w-full h-full">
                                                            <img
                                                                src={desktopPreview}
                                                                alt="Desktop Preview"
                                                                className="w-full h-full object-cover rounded-xl shadow-sm"
                                                            />
                                                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                                                                <span className="text-white text-sm font-medium bg-black/50 px-3 py-1.5 rounded-lg backdrop-blur-sm">Change Image</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center text-gray-500">
                                                            <div className="p-3 bg-gray-100 rounded-full mb-3 group-hover:scale-110 transition-transform">
                                                                <ArrowUpTrayIcon className="h-6 w-6 text-gray-600" />
                                                            </div>
                                                            <span className="font-medium text-sm text-gray-900">Click to upload</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center">
                                                <DevicePhoneMobileIcon className="h-4 w-4 mr-1.5" />
                                                Mobile Banner
                                            </label>
                                            <div className="relative group cursor-pointer">
                                                <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all h-48 flex flex-col justify-center items-center ${mobilePreview ? 'border-gray-300 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
                                                    }`}>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleMobileChange}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    />
                                                    {mobilePreview ? (
                                                        <div className="relative w-full h-full">
                                                            <img
                                                                src={mobilePreview}
                                                                alt="Mobile Preview"
                                                                className="w-full h-full object-cover rounded-xl shadow-sm"
                                                            />
                                                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                                                                <span className="text-white text-sm font-medium bg-black/50 px-3 py-1.5 rounded-lg backdrop-blur-sm">Change Image</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center text-gray-500">
                                                            <div className="p-3 bg-gray-100 rounded-full mb-3 group-hover:scale-110 transition-transform">
                                                                <ArrowUpTrayIcon className="h-6 w-6 text-gray-600" />
                                                            </div>
                                                            <span className="font-medium text-sm text-gray-900">Click to upload</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-4 space-x-3">
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <motion.button
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.99 }}
                                            type="submit"
                                            disabled={loading}
                                            className="bg-gray-900 text-white px-8 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                        >
                                            {loading ? "Uploading..." : "Add Banner"}
                                        </motion.button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Banner List - Table View */}
                <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white mb-8">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50/50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Banner Preview</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Category</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Subcategory</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {banners.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center">
                                            <PhotoIcon className="h-12 w-12 text-gray-300 mb-3" />
                                            <p className="font-medium text-gray-900">No banners found</p>
                                            <p className="text-sm text-gray-500 mt-1">Upload banners for your collections</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                banners.map((banner, index) => {
                                    const category = categories.find(cat => cat.id === banner.category_id);
                                    const subcategory = subcategories.find(sub => sub.id === banner.subcategory_id);
                                    return (
                                        <motion.tr
                                            key={banner.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="hover:bg-gray-50/80 transition-colors"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-4">
                                                    {banner.desktop_banner_url ? (
                                                        <div className="h-16 w-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-100">
                                                            <img src={banner.desktop_banner_url} alt="Desktop" className="h-full w-full object-cover" />
                                                        </div>
                                                    ) : (
                                                        <div className="h-16 w-24 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-400 text-xs">
                                                            No Desktop
                                                        </div>
                                                    )}
                                                    {banner.mobile_banner_url && (
                                                        <div className="h-16 w-10 rounded-lg border border-gray-200 overflow-hidden bg-gray-100">
                                                            <img src={banner.mobile_banner_url} alt="Mobile" className="h-full w-full object-cover" />
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-3 text-gray-500">
                                                        <TagIcon className="h-4 w-4" />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-900">{category?.name || 'Unknown'}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-3 text-gray-500">
                                                        <FolderIcon className="h-4 w-4" />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-900">{subcategory?.name || 'Unknown'}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleDeleteBanner(banner.id)}
                                                    className="text-gray-400 hover:text-red-600 transition-colors p-1.5 hover:bg-red-50 rounded-lg"
                                                    title="Delete Banner"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </motion.tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </motion.div>
    );
};

export default CollectionBannerTab;
